import {
  Flex,
  Typography,
  Button,
  Avatar,
  Space,
  Alert,
} from "antd";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  APIExpert,
  apiExpertToExpert,
  Expert,
  getExperts,
  submitQuestionWithHist,
  getLiveAvatarToken,
  LiveAvatarToken,
} from "../code/utils";
import { useUser } from "../components/AppLayout";
import { ChatBox } from "../components/ChatBox";
import { ChatMessage, ChatMessageProps } from "../components/ChatMessage";
import LiveAvatar, { LiveAvatarRef } from "../components/LiveAvatar";
import { AccessCodeModal } from "../components/AccessCodeModal";
import ExpertIntroVideo from "../components/ExpertIntroVideo";
import { supabase } from "../lib/supabase";
import {
  CheckCircleOutlined,
  HomeOutlined,
  BankOutlined,
  SafetyCertificateOutlined,
  MenuOutlined,
  FireOutlined,
  MessageOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import { config } from "../code/vars";

const { Title } = Typography;

/** Client-side cap for the session countdown; server may end sooner via max_session_duration */
const VIDEO_SESSION_MAX_MS = 30 * 60 * 1000;
/** HeyGen session keep-alive — avoids idle disconnect between questions */
const LIVEAVATAR_KEEPALIVE_INTERVAL_MS = 60 * 1000;

function formatSessionCountdown(remainingMs: number): string {
  const totalSec = Math.max(0, Math.ceil(remainingMs / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const Chat: React.FC = () => {
  const params = useParams();
  const [messageHistory, setMessageHistory] = useState<Array<ChatMessageProps>>(
    []
  );
  const [errors, setErrors] = useState<Array<string>>([]);
  const navigate = useNavigate();
  const { user, profile } = useUser();
  const [experts, setExperts] = useState<{ [key: string]: Expert }>({});
  const [expert, setExpert] = useState<Expert | undefined>(
    experts[params.expert || ""]
  );

  const [stream, setStream] = useState<
    Array<{ sequence: number; duration: number; id: string; url: string }>
  >([]);

  const [showVideo, setShowVideo] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showAccessCodeModal, setShowAccessCodeModal] = useState(false);
  const [isActivated, setIsActivated] = useState<boolean | null>(null);
  const [chatSessionId, setChatSessionId] = useState<string | null>(null);
  const [liveAvatarToken, setLiveAvatarToken] = useState<LiveAvatarToken | null>(null);
  const [avatarReady, setAvatarReady] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isAvatarTalking, setIsAvatarTalking] = useState(false);
  const [sessionRemainingMs, setSessionRemainingMs] = useState<number | null>(null);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const messageContainerRef = useRef<HTMLDivElement>(null);
  const liveAvatarRef = useRef<LiveAvatarRef>(null);

  // Memoize callbacks to prevent infinite re-renders
  const handleAvatarReady = useCallback((sessionId: string) => {
    console.log("LiveAvatar ready:", sessionId);
    setAvatarReady(true);
  }, []);

  const handleAvatarError = useCallback((error: Error) => {
    console.error("LiveAvatar error:", error);
    setErrors((prevErrors) => [...prevErrors, "Failed to initialize avatar"]);
  }, []);

  const handleAvatarTalkingChange = useCallback((isTalking: boolean) => {
    setIsAvatarTalking(isTalking);
  }, []);

  useEffect(() => {
    async function fetchExperts() {
      const tmpExperts = await getExperts();
      const newExperts: { [key: string]: Expert } = {};
      tmpExperts.map(
        (e: APIExpert) => (newExperts[e.id.toString()] = apiExpertToExpert(e))
      );
      setExperts(newExperts);
      setExpert(newExperts[params.expert || ""]);
    }

    fetchExperts();
  }, [params]);

  useEffect(() => {
    // Check activation status
    const checkActivation = async () => {
      if (user?.id) {
        if (profile) {
          setIsActivated(profile.activated);
        } else {
          const { data } = await supabase
            .from("profiles")
            .select("activated")
            .eq("id", user.id)
            .single();

          if (data) {
            setIsActivated(data.activated);
          } else {
            setIsActivated(false);
          }
        }
      } else {
        setIsActivated(false);
      }
    };

    checkActivation();
  }, [user, profile]);

  useEffect(() => {
    // Fetch LiveAvatar session token when video is enabled
    const fetchAccessToken = async () => {
      if (showVideo && !liveAvatarToken) {
        const token = await getLiveAvatarToken();
        setLiveAvatarToken(token);
        setAvatarReady(false); // Reset ready state when getting new token
      } else if (!showVideo) {
        // Clean up when video is disabled
        setLiveAvatarToken(null);
        setAvatarReady(false);
      }
    };

    fetchAccessToken();
  }, [showVideo]); // Remove liveAvatarToken from dependencies to prevent infinite loop

  // LiveAvatar: periodic keep-alive + session countdown (idle-safe, max 30 min UI cap)
  useEffect(() => {
    if (!showVideo || !avatarReady) {
      setSessionRemainingMs(null);
      return;
    }

    const maxSec = liveAvatarRef.current?.getMaxSessionDuration?.() ?? null;
    const serverMs =
      maxSec != null && maxSec > 0 ? maxSec * 1000 : null;
    const durationMs = Math.min(
      VIDEO_SESSION_MAX_MS,
      serverMs ?? VIDEO_SESSION_MAX_MS
    );
    const deadline = Date.now() + durationMs;
    setSessionRemainingMs(durationMs);

    const runKeepAlive = () => {
      void liveAvatarRef.current?.keepAlive?.().catch((err) => {
        console.error("LiveAvatar keepAlive:", err);
      });
    };
    runKeepAlive();

    const keepAliveId = window.setInterval(
      runKeepAlive,
      LIVEAVATAR_KEEPALIVE_INTERVAL_MS
    );

    const tickId = window.setInterval(() => {
      const left = Math.max(0, deadline - Date.now());
      setSessionRemainingMs(left);
      if (left <= 0) {
        window.clearInterval(keepAliveId);
        window.clearInterval(tickId);
        void liveAvatarRef.current?.stopSession?.().finally(() => {
          setShowVideo(false);
          setErrors((prev) => [
            ...prev,
            "Video session ended after the maximum session length.",
          ]);
        });
      }
    }, 1000);

    return () => {
      window.clearInterval(keepAliveId);
      window.clearInterval(tickId);
    };
  }, [showVideo, avatarReady, liveAvatarToken?.session_token]);

  console.log(showVideo, liveAvatarToken);

  async function addMessage(message: ChatMessageProps, swap = false) {
    if (swap) {
      await setMessageHistory((prevState) => {
        return [...prevState.slice(0, -1), message];
      });
    } else {
      await setMessageHistory((prevState) => {
        return [...prevState, message];
      });
    }
  }

  async function addMessages(messages: Array<ChatMessageProps>) {
    await setMessageHistory((prevState) => {
      return [...prevState, ...messages];
    });
  }

  async function submitMessage(question: string, customPrompt: string = "") {
    let sid = chatSessionId;
    if (messageHistory.length === 0) {
      sid = crypto.randomUUID();
      setChatSessionId(sid);
    }
    // Check if user is logged in
    if (!user) {
      setErrors(["Please sign in to chat with experts"]);
      navigate("/signin");
      return;
    }

    // Check if account is activated
    if (isActivated === false) {
      setErrors(["Please activate your account with an access code to chat"]);
      setShowAccessCodeModal(true);
      return;
    }

    setStream([]);

    if (!expert) {
      setErrors(["Please pick an expert..."]);
      return;
    }

    setErrors([]);

    const msgHist = [...messageHistory.map((x) => x.message), question].slice(
      -1 * config.message_history_limit
    );
    console.log(msgHist);

    const newMessage = {
      message: question,
      isexpert: false,
      containerRef: messageContainerRef,
    };

    await addMessages([
      newMessage,
      {
        message: "",
        isexpert: true,
        isPlaceholder: true,
        containerRef: messageContainerRef,
      },
    ]);

    await new Promise((r) => setTimeout(r, 2000));

    // Use LiveAvatar endpoint if video is enabled
    const useLiveAvatar = showVideo && !!liveAvatarToken?.session_token;

    console.log("Submitting question:", {
      showVideo,
      liveAvatarToken,
      useLiveAvatar,
    });

    const answer = await submitQuestionWithHist(
      expert.value,
      msgHist,
      "",
      false, // Don't use old video system
      sid,
      useLiveAvatar, // Use the heygen endpoint which now returns audio
      null,
      null
    );

    const respMessage = {
      message: answer.answer,
      isexpert: true,
      containerRef: messageContainerRef,
    };
    await addMessage(respMessage, true);

    // Send audio chunks to LiveAvatar
    if (useLiveAvatar && liveAvatarRef.current && answer.audio && answer.audio.length > 0) {
      console.log(`Sending ${answer.audio.length} audio chunks to LiveAvatar`);
      for (const audioChunk of answer.audio) {
        try {
          liveAvatarRef.current.repeatAudio(audioChunk);
        } catch (error) {
          console.error("Error sending audio chunk to LiveAvatar:", error);
        }
      }
    }

    if (useLiveAvatar && liveAvatarRef.current) {
      void liveAvatarRef.current.keepAlive().catch(() => {});
    }
  }

  function getAutoSize() {
    return { maxRows: 7, minRows: 1 };
    return messageHistory.length
      ? { maxRows: 7, minRows: 1 }
      : { maxRows: 10, minRows: 4 };
  }

  const [chatPanelOpen, setChatPanelOpen] = useState(false);

  if (!expert) {
    return <div>Loading...</div>;
  }

  console.log(expert);

  const chatPanelWidth = isMobile ? "100%" : "380px";

  // Video Mode Layout - Zoom-like interface
  if (showVideo) {
    return (
      <div
        style={{
          position: "fixed",
          top: 65,
          left: 25,
          right: 25,
          bottom: 25,
          overflow: "hidden",
          display: "flex",
          flexDirection: "row",
        }}
      >
        {/* Left Side - Video + Bottom Panel */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          {/* Video Container */}
          <div
            style={{
              flex: 1,
              backgroundColor: "#1a1a1a",
              position: "relative",
              borderRadius: "10px",
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            {liveAvatarToken?.session_token ? (
              <LiveAvatar
                ref={liveAvatarRef}
                sessionToken={liveAvatarToken.session_token}
                key={`${expert?.value || "default"}-${liveAvatarToken.session_token}`}
                onReady={handleAvatarReady}
                onError={handleAvatarError}
                onAvatarTalkingChange={handleAvatarTalkingChange}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                }}
              >
                Loading avatar...
              </div>
            )}

            {/* Expert Info - Top Left of Video */}
            <Flex
              align="center"
              gap={12}
              style={{
                position: "absolute",
                top: 10,
                left: 10,
                zIndex: 100,
                backgroundColor: "rgba(0,0,0,0.5)",
                padding: "8px 16px",
                borderRadius: 24,
                backdropFilter: "blur(10px)",
              }}
            >
              <Avatar
                size={36}
                src={expert.image || "/1.jpg"}
                style={{ border: "2px solid #fff" }}
              />
              <div>
                <Typography.Text strong style={{ color: "#fff", display: "block", fontSize: 14 }}>
                  {expert.friendlyName}
                </Typography.Text>
                <Typography.Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 11 }}>
                  {isAvatarTalking ? "Speaking..." : avatarReady ? "Connected" : "Connecting..."}
                </Typography.Text>
              </div>
            </Flex>

            {/* End Call Button - Top Right of Video */}
            <Button
              type="primary"
              danger
              icon={<PhoneOutlined style={{ transform: "rotate(225deg)" }} />}
              onClick={() => setShowVideo(false)}
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                borderRadius: 20,
                zIndex: 100,background:"red",
                width: 107
              }}
            >
            </Button>

            {sessionRemainingMs !== null && (
              <div
                style={{
                  position: "absolute",
                  bottom: 16,
                  left: 16,
                  zIndex: 101,
                  backgroundColor: "rgba(0,0,0,0.65)",
                  color: "#fff",
                  padding: "8px 16px",
                  borderRadius: 20,
                  fontSize: 13,
                  fontVariantNumeric: "tabular-nums",
                  backdropFilter: "blur(8px)",
                  pointerEvents: "none",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.35)",
                }}
              >
                {formatSessionCountdown(sessionRemainingMs)} remaining
              </div>
            )}
          </div>

          {/* Bottom Panel - Suggested Questions + Chat Input */}
          <div
            style={{
              backgroundColor: "rgb(251, 243, 242)",
              borderTop: "1px solid rgba(255,255,255,0.1)",
              flexShrink: 0,
            }}
          >
            {/* Suggested Questions */}
            <div style={{ padding: "12px 16px 8px" }}>
              <Flex wrap="wrap" gap={8} justify="center">
                {expert.starters.slice(0, 3).map((starter, index) => (
                  <div
                    key={index}
                    style={{
                      backgroundColor: "rgba(255,255,255,0.1)",
                      color: "#000",
                      border: "1px solid rgba(0,0,0,0.2)",
                      padding: "6px 12px",
                      borderRadius: "16px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "12px",
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => submitMessage(starter.label)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
                    }}
                  >
                    <FireOutlined style={{ color: "#FA991C", fontSize: 11 }} />
                    <span>{starter.label}</span>
                  </div>
                ))}
              </Flex>
            </div>

            {/* Chat Input */}
            <div style={{ padding: "0px" }}>
              {!user && (
                <Alert
                  message="Sign in required"
                  type="warning"
                  showIcon
                  style={{ marginBottom: 8 }}
                  action={
                    <Button size="small" onClick={() => navigate("/signin")}>
                      Sign In
                    </Button>
                  }
                />
              )}
              {user && isActivated === false && (
                <Alert
                  message="Account activation required"
                  type="error"
                  showIcon
                  style={{ marginBottom: 8 }}
                  action={
                    <Button size="small" onClick={() => setShowAccessCodeModal(true)}>
                      Activate
                    </Button>
                  }
                />
              )}
              <ChatBox
                errors={errors}
                onMessage={submitMessage}
                conversationStarted={messageHistory.length > 0}
                autoSize={{ maxRows: 2, minRows: 1 }}
                expert={expert}
                disabled={!user || isActivated === false}
              />
            </div>
          </div>
        </div>

        {/* Chat Panel on the Right - Desktop Only (Full Height) */}
        {!isMobile && (
          <div
            style={{
              width: chatPanelWidth,
              paddingLeft: 10,
            }}
          >
            <div style={{
              border: "1px solid rgba(0,0,0,0.2)", backgroundColor: "#FbF3F2",
              display: "flex",
              flexDirection: "column",
              flexShrink: 0,
              height: "100%",
              borderRadius: 10
            }}>
              {/* Chat Panel Header */}


              {/* Chat Messages */}
              <div
                ref={messageContainerRef}
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "12px",
                }}
              >
                {messageHistory.length === 0 ? (
                  <Flex
                    vertical
                    align="center"
                    justify="center"
                    style={{ height: "100%", textAlign: "center", padding: 16 }}
                  >
                    <MessageOutlined style={{ fontSize: 36, color: "#ccc", marginBottom: 12 }} />
                    <Typography.Text style={{ color: "#666", fontSize: 13 }}>
                      Ask a question to start
                    </Typography.Text>
                  </Flex>
                ) : (
                  messageHistory.map((x, i) => (
                    <ChatMessage
                      containerRef={messageContainerRef}
                      key={"message_" + i.toString() + (x.isPlaceholder ? "_t" : "_f")}
                      message={x.message}
                      isPlaceholder={x.isPlaceholder}
                      isexpert={x.isexpert}
                      expertname={expert ? expert.friendlyName : ""}
                      useWindowScroll={false}
                    />
                  ))
                )}
              </div>

            </div>
          </div>
        )}

        {/* Mobile Chat Toggle Button */}
        {isMobile && (
          <Button
            type="primary"
            icon={<MessageOutlined />}
            onClick={() => setChatPanelOpen(!chatPanelOpen)}
            style={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 20,
              backgroundColor: messageHistory.length > 0 ? "#FA991C" : "#032539",
              borderColor: messageHistory.length > 0 ? "#FA991C" : "#032539",
            }}
          >
            {messageHistory.length > 0 && `(${Math.ceil(messageHistory.length / 2)})`}
          </Button>
        )}

        {/* Mobile Chat Panel Overlay */}
        {isMobile && chatPanelOpen && (
          <>
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 25,
              }}
              onClick={() => setChatPanelOpen(false)}
            />
            <div
              style={{
                position: "absolute",
                top: "10%",
                left: "5%",
                right: "5%",
                bottom: "10%",
                backgroundColor: "#fff",
                borderRadius: 16,
                display: "flex",
                flexDirection: "column",
                zIndex: 30,
                overflow: "hidden",
              }}
            >
              <Flex
                align="center"
                justify="space-between"
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid rgba(3, 37, 57, 0.1)",
                  backgroundColor: "#032539",
                }}
              >
                <Typography.Text strong style={{ color: "#FbF3F2" }}>
                  Chat History
                </Typography.Text>
                <Button
                  type="text"
                  icon={<span style={{ color: "#FbF3F2", fontSize: 18 }}>×</span>}
                  onClick={() => setChatPanelOpen(false)}
                />
              </Flex>
              <div
                ref={messageContainerRef}
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "16px",
                  backgroundColor: "#FbF3F2",
                }}
              >
                {messageHistory.length === 0 ? (
                  <Flex
                    vertical
                    align="center"
                    justify="center"
                    style={{ height: "100%", textAlign: "center" }}
                  >
                    <Typography.Text style={{ color: "#666" }}>
                      No messages yet
                    </Typography.Text>
                  </Flex>
                ) : (
                  messageHistory.map((x, i) => (
                    <ChatMessage
                      containerRef={messageContainerRef}
                      key={"message_" + i.toString() + (x.isPlaceholder ? "_t" : "_f")}
                      message={x.message}
                      isPlaceholder={x.isPlaceholder}
                      isexpert={x.isexpert}
                      expertname={expert ? expert.friendlyName : ""}
                      useWindowScroll={false}
                    />
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {/* Access Code Modal */}
        {user && (
          <AccessCodeModal
            open={showAccessCodeModal}
            onSuccess={async () => {
              setShowAccessCodeModal(false);
              setIsActivated(true);
              setErrors([]);
            }}
            onCancel={() => setShowAccessCodeModal(false)}
            userId={user.id}
          />
        )}
      </div>
    );
  }

  // Non-Video Mode Layout - Message history replaces video area, chat at bottom
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minHeight: "100vh",
        position: "relative",
        background: "#FbF3F2",
      }}
    >
      {/* Main Content Area */}
      <div
        style={{
          flex: 1,
          maxWidth: "767px",
          width: "100%",
          margin: "0 auto",
          padding: "24px",
          paddingBottom: "200px", // Space for bottom chat section
        }}
      >
        {/* 1. Expert Profile Section */}
        <Flex align="flex-start" gap={16} style={{ marginBottom: 32 }}>
          <Avatar
            size={120}
            src={expert.image || "/1.jpg"}
            style={{ borderRadius: "50%" }}
          />
          <Flex vertical style={{ flex: 1 }}>
            <Flex align="center" gap={8} style={{ marginBottom: 0 }}>
              <Title level={2} style={{ margin: 0, color: "#032539" }}>
                {expert.friendlyName}
              </Title>
              <Space size={4}>
                <CheckCircleOutlined style={{ color: "#52c41a" }} />
                <HomeOutlined style={{ color: "#1C768F" }} />
                <BankOutlined style={{ color: "#fa541c" }} />
                <SafetyCertificateOutlined style={{ color: "#52c41a" }} />
              </Space>
            </Flex>
            <Typography.Text
              style={{
                color: "#032539",
                marginBottom: 16,
                fontSize: "16px",
              }}
            >
              {expert.subtitle || "Expert in their field"}
            </Typography.Text>
            <Space>
              {!!expert.heygen_avatar_id && (
                <Button
                  type="primary"
                  icon={<PhoneOutlined />}
                  onClick={() => setShowVideo(true)}
                  style={{
                    backgroundColor: "#032539",
                    borderColor: "#032539",
                    color: "#FbF3F2",
                  }}
                >
                  Video Call
                </Button>
              )}
            </Space>
          </Flex>
        </Flex>

        {/* 2. Intro Video (only when no chat and no video call), then Description OR Message History */}
        {messageHistory.length === 0 ? (
          // Show Intro Video + Description when no messages and not in video call
          <div style={{ marginBottom: 32 }}>
            <ExpertIntroVideo
              videoId={expert?.introVideoId}
              thumbnailUrl={expert?.introVideoThumbnail}
              title={`${expert.friendlyName} introduction`}
            />
            <Flex align="center" gap={8} style={{ marginBottom: 12 }}>
              <MenuOutlined style={{ color: "#032539" }} />
              <Title level={4} style={{ margin: 0, color: "#032539" }}>
                Description
              </Title>
            </Flex>
            <div style={{ position: "relative" }}>
              <Typography.Paragraph
                style={{
                  color: "#032539",
                  fontSize: "16px",
                  lineHeight: 1.6,
                }}
                ellipsis={{
                  rows: 3,
                  expandable: "collapsible",
                  suffix: "",
                  symbol: (expanded) => "",
                  defaultExpanded: false,
                  expanded: showFullDescription,
                  onExpand: (_, info) => setShowFullDescription(info.expanded),
                  onEllipsis: () => { },
                }}
              >
                {expert.summary ||
                  "Ask me anything about my journey from startup founder ($0) to scaleup CEO ($30billion). I'm here to help you on your journey."}
              </Typography.Paragraph>
              {!showFullDescription && (
                <div
                  style={{
                    position: "absolute",
                    bottom: -25,
                    left: 0,
                    right: 0,
                    background: "linear-gradient(to bottom, transparent, #FbF3F2 100%)",
                    paddingTop: "30px",
                  }}
                >
                  <Typography.Link
                    style={{ color: "#1C768F" }}
                    onClick={() => setShowFullDescription(true)}
                  >
                    View more ↓
                  </Typography.Link>
                </div>
              )}
              {showFullDescription && (
                <div style={{ marginTop: 8 }}>
                  <Typography.Link
                    style={{ color: "#1C768F" }}
                    onClick={() => setShowFullDescription(false)}
                  >
                    View less ↑
                  </Typography.Link>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Show Message History when there are messages
          <div
            ref={messageContainerRef}
            style={{
              marginBottom: 32,
              minHeight: "300px",
              maxHeight: "50vh",
              overflowY: "auto",
            }}
          >
            
            {messageHistory.map((x, i) => (
              <ChatMessage
                containerRef={messageContainerRef}
                key={"message_" + i.toString() + (x.isPlaceholder ? "_t" : "_f")}
                message={x.message}
                isPlaceholder={x.isPlaceholder}
                isexpert={x.isexpert}
                expertname={expert ? expert.friendlyName : ""}
                useWindowScroll={false}
              />
            ))}
          </div>
        )}
      </div>

      {/* Fixed Bottom Section - Suggested Questions + Chat Input */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "#fff",
          boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.1)",
          zIndex: 1000,
        }}
      >
        {/* Suggested Questions */}
        <div
          style={{
            maxWidth: "767px",
            margin: "0 auto",
            padding: "12px 24px",
            borderBottom: "1px solid rgba(3, 37, 57, 0.05)",
          }}
        >
          <Flex wrap="wrap" gap={8} justify="center">
            {expert.starters.slice(0, isMobile ? 2 : 3).map((starter, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: "rgba(3, 37, 57, 0.05)",
                  color: "#032539",
                  border: "1px solid rgba(3, 37, 57, 0.1)",
                  padding: "8px 14px",
                  borderRadius: "20px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "13px",
                  transition: "all 0.2s ease",
                }}
                onClick={() => submitMessage(starter.label)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(3, 37, 57, 0.1)";
                  e.currentTarget.style.borderColor = "#032539";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(3, 37, 57, 0.05)";
                  e.currentTarget.style.borderColor = "rgba(3, 37, 57, 0.1)";
                }}
              >
                <FireOutlined style={{ color: "#FA991C" }} />
                <span>{starter.label}</span>
              </div>
            ))}
          </Flex>
        </div>

        {/* Chat Input */}
        <div
          style={{
            maxWidth: "767px",
            margin: "0 auto",
            padding: "12px 24px",
          }}
        >
          {!user && (
            <Alert
              message="Sign in to chat"
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              action={
                <Button size="small" onClick={() => navigate("/signin")}>
                  Sign In
                </Button>
              }
            />
          )}
          {user && isActivated === false && (
            <Alert
              message="Activate account to chat"
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              action={
                <Button size="small" onClick={() => setShowAccessCodeModal(true)}>
                  Activate
                </Button>
              }
            />
          )}
          <ChatBox
            errors={errors}
            onMessage={submitMessage}
            conversationStarted={messageHistory.length > 0}
            autoSize={{ maxRows: 3, minRows: 1 }}
            expert={expert}
            disabled={!user || isActivated === false}
          />
        </div>
      </div>

      {/* Access Code Modal */}
      {user && (
        <AccessCodeModal
          open={showAccessCodeModal}
          onSuccess={async () => {
            setShowAccessCodeModal(false);
            setIsActivated(true);
            setErrors([]);
          }}
          onCancel={() => setShowAccessCodeModal(false)}
          userId={user.id}
        />
      )}
    </div>
  );
};

export default Chat;
